/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
import React, { Fragment, RefObject } from 'react';

import { Moment } from 'moment';

/**
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @version 1.4.0
 * @since 1.0.1
 */
import { IDatePickerLocale } from './CalendarHead';
import { UnlinkedCalendar } from './UnlinkedCalendar';
import { getDefaultState } from './utils';

/**
 * @description Props for calendar picker UI.
 * @interface IRangeDatePickerPickerUI
 *
 * @property{onDatesChange} Render calendar on dates change.
 * @property{component} Current Calendar (UnlinkedCalendar).
 */
export interface IRangeDatePickerPickerUI {
    className?: string;
    component: any;
    endDate?: any;
    locale: IDatePickerLocale;
    onApply: (leftRange: Moment, rightRange: Moment) => void;
    onDatesChange?: any;
    range?: any;
    startDate?: any;
}

interface IRangeDatePickerTime {
    timeLeftHour?: number;
    timeLeftMin?: number;
    timeRightHour?: number;
    timeRightMin?: number;
}

/**
 * PickerUI component.
 *
 * IRangeDatePickerPickerUI: IRangeDatePickerCalendarTitle.
 * Render whole date range picker.
 */
export default class PickerUI extends React.Component<IRangeDatePickerPickerUI> {

    public state = {
        ...getDefaultState(),
        endDate: this.props.endDate.clone(),
        range: this.props.range.clone(),
        startDate: this.props.startDate.clone(),
    };

    public unlinkedCalendarRef = React.createRef<UnlinkedCalendar>();

    public getTimeRange = (): IRangeDatePickerTime => {
        const unlinkedCalendar: RefObject<UnlinkedCalendar> = this.unlinkedCalendarRef;
        let time: IRangeDatePickerTime = {};
        if (unlinkedCalendar.current) {
            const state = unlinkedCalendar.current.state;
            time = { ...state };
        }
        return time;
    };

    /**
     * onDayMouseEnter - handle on cell mouse enter.
     *
     * @param day
     */
    public onDayMouseEnter = (day: Moment) => {
        const { startDate, endDate } = this.state;
        const range = (day >= startDate) ? endDate || day : startDate;
        this.setState({ range });
    };

    /**
     * onDayClick - handle on cell click.
     * @param day
     * @param isLeft
     */
    public onDayClick = (day: Moment, isLeft: boolean) => {
        let { endDate, startDate } = this.state;
        isLeft ? startDate = day : endDate = day;
        if (isLeft && day >= endDate) {
            endDate = startDate.clone();
        }
        const range = endDate || startDate;
        this.setState({
            endDate,
            range,
            startDate,
        });
    };

    public render() {
        const { onDayClick, onDayMouseEnter } = this;
        const props = {
            ...this.state, ...this.props,
            endDate: this.state.endDate,
            onDayClick,
            onDayMouseEnter,
            range: this.state.range,
            startDate: this.state.startDate,
        };
        const { component: Component } = this.props;
        return (
            <Fragment>
                <Component {...props} ref={this.unlinkedCalendarRef}/>
            </Fragment>
        );
    }
}
